import { httpClient } from './http';

export interface Responsible {
  id: string;
  email: string;
  restaurantId: string;
  roleName: 'manager' | 'kitchen';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  createdAt: string;
  updatedAt: string;
  registrationStatus?: 'pending' | 'registered';
}

export interface CreateResponsibleData {
  email: string;
  role_name: 'manager' | 'kitchen';
}

export interface UpdateResponsibleData {
  email?: string;
  role_name?: 'manager' | 'kitchen';
  status?: 'active' | 'inactive' | 'suspended' | 'pending';
}

export interface ResponsibleListResponse {
  data: Responsible[];
  pagination: {
    page: number;
    size: number;
    total: number;
    pages: number;
  };
}

class ResponsiblesService {
  async getResponsibles(
    userId: string,
    restaurantId: string,
    page: number = 1,
    size: number = 20,
    status?: string,
    search?: string
  ): Promise<ResponsibleListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      if (status) params.append('status', status);
      if (search) params.append('search', search);

      const response = await httpClient.get<Responsible[]>(
        `/system/users/${userId}/restaurants/${restaurantId}/responsibles?${params.toString()}`,
        undefined,
        'authenticated'
      );
      
      // Convertir la respuesta directa del array a la estructura esperada
      return {
        data: response,
        pagination: {
          page,
          size,
          total: response.length,
          pages: Math.ceil(response.length / size)
        }
      };
    } catch (error) {
      console.error('Error fetching responsibles:', error);
      throw new Error('Error al obtener responsables');
    }
  }

  async getResponsibleById(userId: string, restaurantId: string, responsibleId: string): Promise<Responsible> {
    try {
      const response = await httpClient.get<Responsible>(
        `/system/users/${userId}/restaurants/${restaurantId}/responsibles/${responsibleId}`,
        undefined,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error fetching responsible:', error);
      throw new Error('Error al obtener responsable');
    }
  }

  async createResponsible(userId: string, restaurantId: string, responsibleData: CreateResponsibleData): Promise<Responsible> {
    try {
      const response = await httpClient.post<Responsible>(
        `/system/users/${userId}/restaurants/${restaurantId}/responsibles`,
        responsibleData,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error creating responsible:', error);
      throw new Error('Error al crear responsable');
    }
  }

  async updateResponsible(
    userId: string,
    restaurantId: string,
    responsibleId: string,
    responsibleData: UpdateResponsibleData
  ): Promise<Responsible> {
    try {
      const response = await httpClient.put<Responsible>(
        `/system/users/${userId}/restaurants/${restaurantId}/responsibles/${responsibleId}`,
        responsibleData,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error updating responsible:', error);
      throw new Error('Error al actualizar responsable');
    }
  }

  async deleteResponsible(userId: string, restaurantId: string, responsibleId: string): Promise<Responsible> {
    try {
      const response = await httpClient.delete<Responsible>(
        `/system/users/${userId}/restaurants/${restaurantId}/responsibles/${responsibleId}`,
        'authenticated'
      );
      return response;
    } catch (error) {
      console.error('Error deleting responsible:', error);
      throw new Error('Error al eliminar responsable');
    }
  }

  async getResponsiblesByRestaurant(
    userId: string,
    restaurantId: string
  ): Promise<{ data: Responsible[]; total: number }> {
    try {
      const response = await httpClient.get<Responsible[]>(
        `/system/users/${userId}/restaurants/${restaurantId}/responsibles`,
        undefined,
        'authenticated'
      );
      
      return {
        data: response,
        total: response.length
      };
    } catch (error) {
      console.error('Error fetching responsibles by restaurant:', error);
      throw new Error('Error al obtener responsables del restaurante');
    }
  }
}

export const responsiblesService = new ResponsiblesService(); 